/**
 * Competitor Slide
 *
 * Slide 4: "Deep Competitor Research" - Shows competitor analysis summary.
 * Matches the UI style from /marketing/competitor-analysis with compact cards.
 */

import { motion } from 'framer-motion'
import { Search, Globe, TrendingUp, Users } from 'lucide-react'
import { cn } from '@trafiflow/shared-ui'
import type { OnboardingArtifacts } from '../../../contexts/OnboardingContext'
import type { CompAnalysisCompetitor } from '@/services/competitor-analysis/types'

interface CompetitorSlideProps {
  artifacts: OnboardingArtifacts
}

// Category badge colors matching competitor-analysis page
const categoryColors: Record<string, string> = {
  'Direct': 'bg-red-500/10 text-red-500',
  'Indirect': 'bg-amber-500/10 text-amber-500',
  'Aspirational': 'bg-purple-500/10 text-purple-500',
  'Industry Leader': 'bg-blue-500/10 text-blue-500',
  'Emerging': 'bg-emerald-500/10 text-emerald-500',
  'Alternative': 'bg-slate-500/10 text-slate-400',
  'Direct Competitor': 'bg-red-500/10 text-red-500',
  'Indirect Competitor': 'bg-amber-500/10 text-amber-500',
  'Aspirational Brand': 'bg-purple-500/10 text-purple-500',
}

export default function CompetitorSlide({ artifacts }: CompetitorSlideProps) {
  // Prefer rich competitor analysis data from the dedicated pipeline
  const competitorAnalysis = artifacts.competitorAnalysis

  // Fallback to patterns artifact (competitor_intelligence stage from ad-generator)
  const patterns = artifacts.patterns as {
    competitors_count?: number
    competitors?: Array<{ name?: string; domain?: string }>
  } | null

  // Get competitors from either source
  const competitors: CompAnalysisCompetitor[] = competitorAnalysis?.competitors ||
    (patterns?.competitors?.map((c, i) => ({
      id: `fallback-${i}`,
      name: c.name || 'Unknown',
      website: c.domain,
      category: 'Direct',
      confidence_score: 0.7,
    })) || [])

  const competitorCount = competitors.length || patterns?.competitors_count || 0
  const summary = competitorAnalysis?.summary
  const marketInsights = competitorAnalysis?.market_insights

  // Debug logging
  console.log('[CompetitorSlide] competitorAnalysis:', competitorAnalysis)
  console.log('[CompetitorSlide] patterns:', patterns)
  console.log('[CompetitorSlide] competitors:', competitors)

  return (
    <div className="flex flex-col items-center text-center max-w-xl mx-auto">
      {/* Icon */}
      <motion.div
        initial={{ scale: 0 }}
        animate={{ scale: 1 }}
        transition={{ delay: 0.2, type: 'spring' }}
        className="w-16 h-16 rounded-2xl bg-gradient-to-br from-emerald-500 to-teal-600 flex items-center justify-center mb-6 shadow-lg"
      >
        <Search className="w-8 h-8 text-white" />
      </motion.div>

      {/* Title */}
      <motion.h2
        initial={{ y: 20, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ delay: 0.3 }}
        className="text-2xl font-bold text-tf-text mb-4"
      >
        Deep Competitor Research
      </motion.h2>

      {/* Summary Banner - matches competitor-analysis page */}
      {summary && (
        <motion.div
          initial={{ y: 20, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ delay: 0.4 }}
          className="w-full mb-5 px-4 py-3 rounded-xl bg-gradient-to-r from-purple-500/10 to-indigo-500/10 border border-purple-500/20"
        >
          <p className="text-sm text-tf-text leading-relaxed">{summary}</p>
        </motion.div>
      )}

      {/* Stats row */}
      <motion.div
        initial={{ y: 20, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ delay: 0.5 }}
        className="flex items-center justify-center gap-6 mb-5"
      >
        <div className="flex items-center gap-2">
          <div className="w-9 h-9 rounded-lg bg-purple-500/10 flex items-center justify-center">
            <Users className="w-4 h-4 text-purple-500" />
          </div>
          <div className="text-left">
            <p className="text-xl font-bold text-tf-text">{competitorCount}</p>
            <p className="text-xs text-tf-text-muted">Competitors</p>
          </div>
        </div>
        {marketInsights?.competitive_level && (
          <div className="flex items-center gap-2">
            <div className="w-9 h-9 rounded-lg bg-amber-500/10 flex items-center justify-center">
              <TrendingUp className="w-4 h-4 text-amber-500" />
            </div>
            <div className="text-left">
              <p className={cn(
                'text-xl font-bold capitalize',
                marketInsights.competitive_level === 'intense' && 'text-red-500',
                marketInsights.competitive_level === 'high' && 'text-amber-500',
                marketInsights.competitive_level === 'moderate' && 'text-yellow-500',
                marketInsights.competitive_level === 'low' && 'text-emerald-500',
              )}>
                {marketInsights.competitive_level}
              </p>
              <p className="text-xs text-tf-text-muted">Competition</p>
            </div>
          </div>
        )}
      </motion.div>

      {/* Competitor Cards - matching competitor-analysis page style */}
      {competitors.length > 0 && (
        <motion.div
          initial={{ y: 20, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ delay: 0.6 }}
          className="w-full space-y-2"
        >
          <h3 className="text-xs font-medium text-tf-text-muted text-left flex items-center gap-1.5 mb-2">
            <Users className="w-3.5 h-3.5 text-purple-500" />
            Discovered Competitors
          </h3>
          {competitors.slice(0, 4).map((competitor, i) => (
            <motion.div
              key={competitor.id || competitor.name}
              initial={{ x: -20, opacity: 0 }}
              animate={{ x: 0, opacity: 1 }}
              transition={{ delay: 0.7 + i * 0.1 }}
              className="flex items-center gap-3 px-3 py-2.5 rounded-lg bg-tf-bg-card border border-tf-border"
            >
              {/* Globe icon */}
              <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-purple-500/20 to-indigo-500/20 flex items-center justify-center flex-shrink-0">
                <Globe className="w-4 h-4 text-purple-500" />
              </div>

              {/* Name and website */}
              <div className="flex-1 min-w-0 text-left">
                <div className="flex items-center gap-2">
                  <p className="text-sm font-medium text-tf-text truncate">{competitor.name}</p>
                  <span className={cn(
                    'px-2 py-0.5 rounded-full text-xs flex-shrink-0',
                    categoryColors[competitor.category] || 'bg-tf-bg-muted text-tf-text-muted'
                  )}>
                    {competitor.category}
                  </span>
                </div>
                {competitor.website && (
                  <p className="text-xs text-tf-text-muted truncate">{competitor.website}</p>
                )}
              </div>

              {/* Match percentage */}
              <span className="text-xs text-tf-text-muted flex-shrink-0">
                {Math.round(competitor.confidence_score * 100)}% match
              </span>
            </motion.div>
          ))}
          {competitors.length > 4 && (
            <motion.p
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 1.1 }}
              className="text-xs text-tf-text-muted text-center pt-1"
            >
              +{competitors.length - 4} more competitors identified
            </motion.p>
          )}
        </motion.div>
      )}

      {/* No competitors fallback */}
      {competitors.length === 0 && (
        <motion.div
          initial={{ y: 20, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ delay: 0.6 }}
          className="w-full py-8 text-center"
        >
          <p className="text-sm text-tf-text-muted">
            Competitor analysis in progress...
          </p>
          <p className="text-xs text-tf-text-muted mt-2">
            This data will be available shortly
          </p>
        </motion.div>
      )}

      {/* Key trends if available */}
      {marketInsights?.key_trends && marketInsights.key_trends.length > 0 && (
        <motion.div
          initial={{ y: 20, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ delay: 1.2 }}
          className="flex flex-wrap justify-center gap-2 mt-4"
        >
          {marketInsights.key_trends.slice(0, 3).map((trend, i) => (
            <span
              key={i}
              className="px-2.5 py-1 text-xs bg-blue-500/10 text-blue-500 rounded-full"
            >
              {trend}
            </span>
          ))}
        </motion.div>
      )}
    </div>
  )
}
